import { Link } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import type { CardProduct } from '../types'
import { useCart } from '../lib/cart'
import { useToast } from '../lib/toast'
import { useT } from '../lib/i18n'

interface UpsellRowProps {
  products: CardProduct[]
  title?: string
  compact?: boolean
}

/** Horizontal "you might also like" strip with quick-add. */
export function UpsellRow({ products, title, compact = false }: UpsellRowProps) {
  const { addItem, lines } = useCart()
  const { toast } = useToast()
  const t = useT()

  const inCart = new Set(lines.map((l) => l.product_id))
  const available = products.filter((p) => !inCart.has(p.id) && p.in_stock).slice(0, 6)
  if (available.length === 0) return null

  const add = (p: CardProduct) => {
    addItem({
      product_id: p.id,
      slug: p.slug,
      name: p.name,
      price: p.price.amount,
      image_url: p.image_url,
    })
    toast(t('common.add_to_cart'))
  }

  return (
    <div>
      <h3 className="mb-3 font-bold text-ink-900">{title ?? t('upsell.title')}</h3>
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
        {available.map((p) => (
          <div
            key={p.id}
            className={`shrink-0 ${compact ? 'w-36' : 'w-44'} rounded-xl border border-ink-100 bg-white p-2`}
          >
            <Link href={`/p/${p.slug}`} className="block aspect-square overflow-hidden rounded-lg bg-ink-50">
              {p.image_url && (
                <img src={p.image_url} alt={p.name} loading="lazy" className="size-full object-cover" />
              )}
            </Link>
            <Link
              href={`/p/${p.slug}`}
              className="mt-2 line-clamp-1 block text-sm font-semibold text-ink-900 hover:text-accent-600"
            >
              {p.name}
            </Link>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-sm font-bold">{p.price.formatted}</span>
              <button
                onClick={() => add(p)}
                aria-label={t('common.add_to_cart')}
                className="flex size-7 items-center justify-center rounded-full bg-ink-900 text-white transition-colors hover:bg-accent-500"
              >
                <Plus className="size-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
